import React, { useState, useEffect, useRef } from 'react'
import { fetchMonths, fetchMonthsCurrent, getDistrict, getDistrictByZoneId, getStates, getZonesAll } from '../../../services/monthlyAttendanceService';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { capitalizeFirstLetter, isDigit } from '../../../utils/validations';
import Select from "react-select";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './EmployeeLeaveCountInaMonth.css'
import { gettingEmployeeLeaveCountInaMonth, gettingEmployeeLeavesFullDetails } from '../../../services/leaveService';
import { format } from 'date-fns';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const EmployeeLeaveCountInaMonth = () => {

    const [state, setState] = useState(3);
    const [district, setDistrict] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [zoneOptions, setZoneOptions] = useState([]);
    const [zoneId, setSelectedZone] = useState(null);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [monthOptions, setMonthOptions] = useState([]);
    const [error, setErrors] = useState('');
    const [loading, setLoading] = useState({ zones: true, districts: false, employees: false, drilldown: false });
    const effectRan = useRef(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const yearInputRef = useRef(null);
    const [leaveNumber, setLeaveNumber] = useState('');

    const [employeeLeaveCount, setEmployeeLeaveCount] = useState([]);
    const [employeeLeavesFullDetailsonLeave, setEmployeeLeavesFullDetailsonLeave] = useState(null);
    const [showDrilldown, setShowDrilldown] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const userRole = localStorage.getItem("role");
    const itmDistrictId = localStorage.getItem("districtId");
    const itmZoneId = localStorage.getItem("zoneId");
    const isITM = userRole === "ITM" || userRole === "itm";
    const isPM = userRole === "PM" || userRole === 'pm';
    const isAPM_Zone = userRole === "APM_Zone" || userRole === "apm_zone" || userRole === "APM_ZONE";

    const isZoneRestricted = isITM || isAPM_Zone;

    const currentYear = new Date().getFullYear();
    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);

    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        leaveNumber: false,
        year: false,
        month: false,
        zoneId: false
    });

    const isFetchDataEnabled = state && year && month && leaveNumber;

    const getSelectedFilters = () => {
        const filters = {};
        
        const stateLabel = stateOptions.find(opt => opt.value === state)?.label;
        if (stateLabel) filters.state = stateLabel;
        
        const districtLabel = districtOptions.find(opt => opt.value === district)?.label;
        if (districtLabel) filters.district = districtLabel;
        
        const zoneLabel = zoneOptions.find(opt => opt.value === zoneId)?.label;
        if (zoneLabel) filters.zone = zoneLabel;
        
        if (leaveNumber) filters.leaveThreshold = `${leaveNumber}`;
        if (year) filters.year = year;
        if (month) filters.month = getMonthName(month);
        
        return filters;
    };

    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        }
    }, []);

    // 1. Fetch Zones with frontend "ALL" option
    useEffect(() => {
        const fetchZones = async () => {
            try {
                setLoading(prev => ({ ...prev, zones: true }));
                const data = await getZonesAll();
                const fetchedZones = data.map(zone => ({
                    value: zone.zoneId,
                    label: zone.zoneName,
                    districts: zone.districts || []
                }));

                // Add "ALL" option if user is not zone-restricted
                const zones = isZoneRestricted
                    ? fetchedZones
                    : [{ value: 'ALL', label: 'ALL' }, ...fetchedZones];

                setZoneOptions(zones);

                // Auto-select zone for restricted users
                if (isZoneRestricted && itmZoneId) {
                    const matchedZone = fetchedZones.find(zone => String(zone.value) === String(itmZoneId));
                    if (matchedZone) {
                        setSelectedZone(matchedZone.value);
                    } else {
                        console.warn('Assigned zone not found in zone list');
                        setSelectedZone(null);
                    }
                }
            } catch (error) {
                console.error("Error fetching zones:", error);
                setMessage({ text: "Failed to load zones", type: "error" });
            } finally {
                setLoading(prev => ({ ...prev, zones: false }));
            }
        };
        fetchZones();
    }, [isZoneRestricted, itmZoneId]);

    // 2. Fetch Districts based on Zone (or State if "ALL" selected)
    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                let districtsData = [];

                // ITM users: show only their assigned district
                if (isITM && itmDistrictId) {
                    if (state) {
                        districtsData = await getDistrict(state);
                    } else {
                        setDistrictOptions([]);
                        setDistrict(null);
                        return;
                    }

                    const filteredDistricts = districtsData.filter(
                        item => String(item.districtId) === String(itmDistrictId)
                    );

                    setDistrictOptions(
                        filteredDistricts.map(item => ({
                            value: item.districtId,
                            label: item.districtName,
                        }))
                    );

                    if (filteredDistricts.length > 0) {
                        setDistrict(filteredDistricts[0].districtId);
                    } else {
                        setDistrictOptions([]);
                        setDistrict(null);
                        setErrors(prev => ({
                            ...prev,
                            general: 'Your assigned district could not be found'
                        }));
                    }
                    return;
                }

                // If "ALL" is selected or no specific zone is selected: get all districts for the state
                if (zoneId === 'ALL') {
                    if (state) {
                        districtsData = await getDistrict(state);
                    } else {
                        districtsData = [];
                    }
                } else if (zoneId && zoneId !== 'null' && zoneId !== 'undefined') {
                    // Specific Zone selected: get districts by zone ID
                    const response = await getDistrictByZoneId(zoneId);
                    districtsData = response?.districts || response || [];
                } else {
                    setDistrictOptions([]);
                    setDistrict(null);
                    return;
                }

                setDistrictOptions(
                    districtsData.map(item => ({
                        value: item.districtId,
                        label: item.districtName,
                    }))
                );
                setDistrict(null);

            } catch (error) {
                console.error('Error fetching districts:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
            }
        };

        fetchDistricts();
    }, [state, zoneId, isITM, isAPM_Zone, itmDistrictId, isPM]);

    const handleYearChange = (date) => {
        const newYear = date ? date.getFullYear() : null;
        setYear(newYear);
        setMonth(null);
        setErrors(prev => ({ ...prev, year: null }));
    };

    useEffect(() => {
        const loadMonths = async () => {
            try {
                const months = await fetchMonthsCurrent(year);
                setMonthOptions(months);
            } catch (error) {
                console.error("Error fetching months:", error);
                setErrors((prev) => ({ ...prev, general: "Failed to load months" }));
            }
        };
        loadMonths();
    }, [year]);

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                break;
            case 'district':
                if (!isITM) {
                    setDistrict(value);
                }
                break;
            case 'month':
                setMonth(value);
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleClearAll = () => {
        setState(3);
        setDistrict(null);

        if (isZoneRestricted && itmZoneId) {
            const existingZone = zoneOptions.find(z => String(z.value) === String(itmZoneId));
            setSelectedZone(existingZone ? existingZone.value : null);
        } else {
            setSelectedZone(null);
        }

        setMonth(null);
        setYear(null);
        setLeaveNumber('');
        setEmployeeLeaveCount([]);
        setEmployeeLeavesFullDetailsonLeave(null);
        setShowDrilldown(false);
        setSelectedEmployee(null);
        setErrors('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const leaveNumberCon = Number(leaveNumber);
            // Send null instead of 'ALL' to the API
            const payloadZoneId = zoneId === 'ALL' ? null : zoneId;
            const data = await gettingEmployeeLeaveCountInaMonth(state, district, year, month, payloadZoneId, leaveNumberCon);

            if (data) {
                setEmployeeLeaveCount(data);
                setShowDrilldown(false);
                setEmployeeLeavesFullDetailsonLeave(null);
                setSelectedEmployee(null);
            } else {
                setEmployeeLeaveCount([]);
            }
        } catch (error) {
            console.error("Error fetching report data:", error);
            setEmployeeLeaveCount([]);
        }
    };

    const handleDrilldownDetails = async (employee) => {
        if (!employee?.employeeUniqueId || !employee?.month || !employee?.year) return;

        setLoading(prev => ({ ...prev, drilldown: true }));

        try {
            const { employeeUniqueId, month, year } = employee;
            const data = await gettingEmployeeLeavesFullDetails(employeeUniqueId, month, year);

            setEmployeeLeavesFullDetailsonLeave(data);
            setSelectedEmployee(employee);
            setShowDrilldown(true);
        } catch (error) {
            console.error("Error fetching drilldown data:", error);
            setMessage({ text: "Failed to load employee details", type: "error" });
        } finally {
            setLoading(prev => ({ ...prev, drilldown: false }));
        }
    };

    const handleBackToMainTable = () => {
        setShowDrilldown(false);
        setEmployeeLeavesFullDetailsonLeave(null);
        setSelectedEmployee(null);
    };

    const getMonthName = (monthNumber) => {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        return monthNames[monthNumber - 1] || monthNumber;
    };

    const getStatusBadgeClass = (status) => {
        if (!status) return '';
        const statusLower = status.toLowerCase();
        if (statusLower === 'absent') return 'status-badge-absent';
        if (statusLower === 'leave') return 'status-badge-leave';
        return 'status-badge-other';
    };

    // ==================== CLEAN TEXT PDF EXPORT FUNCTION ====================
    const exportToPDF = (tableData, headers, title, fileName, extraInfo = null) => {
        if (!tableData || tableData.length === 0) {
            alert('No data available to export');
            return;
        }

        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 14;
        const maxWidth = pageWidth - (margin * 2);
        const filters = getSelectedFilters();
        const reportTime = format(new Date(), 'dd-MM-yyyy HH:mm:ss');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(15);
        doc.setTextColor(12, 92, 108);
        doc.text(title, pageWidth / 2, 14, { align: 'center' });

        let filterParts = [];
        if (filters.state) filterParts.push(`State: ${filters.state}`);
        filterParts.push(`Zone: ${filters.zone && filters.zone !== 'N/A' ? filters.zone : 'N/A'}`);
        filterParts.push(`District: ${filters.district && filters.district !== 'N/A' ? filters.district : 'N/A'}`);
        if (filters.month) filterParts.push(`Month: ${filters.month}`);
        if (filters.year) filterParts.push(`Year: ${filters.year}`);
        if (filters.leaveThreshold) filterParts.push(`Leave >= (in Days): ${filters.leaveThreshold}`);
        if (extraInfo) filterParts.push(extraInfo);

        const filterText = filterParts.length > 0 ? filterParts.join(' | ') : 'No filters selected';

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(70, 70, 70);

        const filterLines = doc.splitTextToSize(filterText, maxWidth);
        let yPos = 21;

        filterLines.forEach((line) => {
            doc.text(line, pageWidth / 2, yPos, { align: 'center' });
            yPos += 4.5;
        });

        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(110, 110, 110);
        doc.text(`Generated: ${reportTime}`, pageWidth - margin, yPos + 1, { align: 'right' });

        const startY = yPos + 5;
        const tableRows = tableData.map(row => {
            return headers.map(header => {
                let value = row[header.key];
                if (header.key === 'date' && value) {
                    return format(new Date(value), 'dd-MM-yyyy');
                }
                if (typeof value === 'number') return value;
                return value || '-';
            });
        });

        doc.autoTable({
            head: [headers.map(h => h.label)],
            body: tableRows,
            startY: startY,
            theme: 'grid',
            styles: {
                fontSize: 8.5,
                cellPadding: 2.2,
                valign: 'middle',
                halign: 'center',
                lineColor: [210, 210, 210],
                lineWidth: 0.1,
                font: 'helvetica',
                textColor: [40, 40, 40]
            },
            headStyles: {
                fillColor: [12, 92, 108],
                textColor: [255, 255, 255],
                fontSize: 9,
                fontStyle: 'bold',
                halign: 'center',
                cellPadding: 2.8,
                lineColor: [12, 92, 108]
            },
            alternateRowStyles: {
                fillColor: [248, 250, 252]
            },
            margin: { left: margin, right: margin, bottom: 12 },
            didDrawPage: (data) => {
                const pageCount = doc.internal.getNumberOfPages();
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(130, 130, 130);
                doc.text(
                    `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
                    pageWidth - margin,
                    doc.internal.pageSize.getHeight() - 5,
                    { align: 'right' }
                );
            }
        });

        doc.save(`${fileName}.pdf`);
    };

    // ==================== CLEAN EXCEL EXPORT FUNCTION ====================
    const exportToExcel = async (tableData, headers, title, fileName, extraInfo = null) => {
        if (!tableData || tableData.length === 0) {
            alert('No data available to export');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Report');
        const filters = getSelectedFilters();
        const reportTime = format(new Date(), 'dd-MM-yyyy HH:mm:ss');

        const totalCols = headers.length;

        worksheet.mergeCells(1, 1, 1, totalCols);
        const titleCell = worksheet.getCell(1, 1);
        titleCell.value = title;
        titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FF0C5C6C' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 30;

        worksheet.mergeCells(2, 1, 2, totalCols);
        const filterCell = worksheet.getCell(2, 1);
        
        let filterParts = [];
        if (filters.state) filterParts.push(`State: ${filters.state}`);
        if (filters.zone && filters.zone !== 'N/A') filterParts.push(`Zone: ${filters.zone}`);
        if (filters.district && filters.district !== 'N/A') filterParts.push(`District: ${filters.district}`);
        if (filters.month) filterParts.push(`Month: ${filters.month}`);
        if (filters.year) filterParts.push(`Year: ${filters.year}`);
        if (filters.leaveThreshold) filterParts.push(`Leave ≥ (in Days): ${filters.leaveThreshold}`);
        
        let filterText = filterParts.join('  |  ');
        if (extraInfo) {
            filterText += `  |  ${extraInfo}`;
        }
        if (!filterText) {
            filterText = 'No filters selected';
        }

        filterCell.value = filterText;
        filterCell.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF555555' } };
        filterCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(2).height = 22;

        worksheet.mergeCells(3, 1, 3, totalCols);
        const timeCell = worksheet.getCell(3, 1);
        timeCell.value = `Generated: ${reportTime}`;
        timeCell.font = { name: 'Arial', size: 9, italic: true, color: { argb: 'FF777777' } };
        timeCell.alignment = { horizontal: 'right', vertical: 'middle' };
        worksheet.getRow(3).height = 20;

        worksheet.addRow([]);

        const headerRow = worksheet.addRow(headers.map(h => h.label));
        headerRow.height = 26;
        headerRow.eachCell((cell) => {
            cell.font = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF0C5C6C' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFB0BEC5' } },
                left: { style: 'thin', color: { argb: 'FFB0BEC5' } },
                bottom: { style: 'thin', color: { argb: 'FFB0BEC5' } },
                right: { style: 'thin', color: { argb: 'FFB0BEC5' } }
            };
        });

        tableData.forEach((row, rowIndex) => {
            const rowData = headers.map(header => {
                let value = row[header.key];
                if (header.key === 'date' && value) {
                    return format(new Date(value), 'dd-MM-yyyy');
                }
                if (typeof value === 'number') return value;
                return value || '-';
            });

            const addedRow = worksheet.addRow(rowData);
            addedRow.height = 22;

            addedRow.eachCell((cell) => {
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.font = { name: 'Arial', size: 9 };
                if (rowIndex % 2 === 1) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFF5F8FA' }
                    };
                }
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
                };
            });
        });

        worksheet.columns.forEach((column, colIndex) => {
            let maxLength = headers[colIndex]?.label?.length || 10;
            column.eachCell({ includeEmpty: false }, (cell, rowNumber) => {
                if (rowNumber > 4) {
                    const cellLen = cell.value ? cell.value.toString().length : 0;
                    if (cellLen > maxLength) maxLength = cellLen;
                }
            });
            column.width = Math.min(Math.max(maxLength + 25, 34), 50);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${fileName}.xlsx`);
    };

    // ==================== EXPORT HANDLERS ====================
    const handleMainTableExport = (type) => {
        if (!employeeLeaveCount || employeeLeaveCount.length === 0) {
            alert('No data available to export');
            return;
        }

        const headers = [
            { key: 'sno', label: 'S.No' },
            { key: 'districtName', label: 'District' },
            { key: 'employeeName', label: 'Employee Name' },
            { key: 'contactNumber', label: 'Contact Number' },
            { key: 'designation', label: 'Designation' },
            { key: 'totalAbsentLeaveCount', label: 'Total' }
        ];

        const data = employeeLeaveCount.map((row, index) => ({
            ...row,
            sno: index + 1
        }));

        const title = 'Employee Leave Report';
        const fileName = `Employee_Leave_Report_${format(new Date(), 'dd-MM-yyyy')}`;

        if (type === 'pdf') {
            exportToPDF(data, headers, title, fileName);
        } else {
            exportToExcel(data, headers, title, fileName);
        }
    };

    const handleDrilldownExport = (type) => {
        if (!employeeLeavesFullDetailsonLeave || !employeeLeavesFullDetailsonLeave.absentLeaveDates || employeeLeavesFullDetailsonLeave.absentLeaveDates.length === 0) {
            alert('No data available to export');
            return;
        }

        const headers = [
            { key: 'sno', label: 'S.No' },
            { key: 'date', label: 'Date' },
            { key: 'status', label: 'Status' }
        ];

        const data = employeeLeavesFullDetailsonLeave.absentLeaveDates.map((row, index) => ({
            ...row,
            sno: index + 1,
            date: row.date,
            status: row.status || 'N/A'
        }));

        const title = `Employee Leave Details - ${selectedEmployee?.employeeName || 'Employee'}`;
        const fileName = `Employee_Leave_Details_${selectedEmployee?.employeeName || 'Employee'}_${format(new Date(), 'dd-MM-yyyy')}`;
    const extraInfo = `Employee: ${selectedEmployee?.employeeName || ''} - ${selectedEmployee.contactNumber} - (${selectedEmployee?.employeeUniqueId || ''})`;

        if (type === 'pdf') {
            exportToPDF(data, headers, title, fileName, extraInfo);
        } else {
            exportToExcel(data, headers, title, fileName, extraInfo);
        }
    };

    return (
        <div>
            <div className="employee-more-leave-container">
                <div className="employee-more-leave-box">
                    <h2 className="employee-more-leave-heading">Employee Leaves In a Month</h2>

                    <form onSubmit={handleSubmit}>
                        <div className="employee-more-leave-maincontainer">
                            <div className="employee-more-leave-container-part">
                                <div className={`employee-more-leave-floating-label-container ${state ? 'float-label' : ''}`}>
                                    <Select
                                        name="state"
                                        options={stateOptions}
                                        value={stateOptions.find(opt => opt.value === state) || null}
                                        onChange={option => handleInputChange(option, 'state')}
                                        placeholder=" "
                                        isClearable={!isITM}
                                        isDisabled={isITM || isZoneRestricted}
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                    <label><span className="required">*</span>State</label>
                                </div>
                            </div>

                            <div className="employee-more-leave-container-part">
                                <div className={`employee-more-leave-floating-label-container ${isFocused?.zoneId || zoneId ? 'float-label' : ''}`}>
                                    <Select
                                        options={zoneOptions}
                                        value={zoneOptions.find(z => z.value === zoneId) || null}
                                        onChange={(opt) => setSelectedZone(opt ? opt.value : null)}
                                        onFocus={() => setIsFocused(prev => ({ ...prev, zoneId: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, zoneId: false }))}
                                        placeholder=" "
                                        isClearable={!isZoneRestricted}
                                        isDisabled={isZoneRestricted}
                                        className="zone-select"
                                        styles={selectStyles}
                                    />
                                    <label>Zone {isZoneRestricted && <span className="required">*</span>}</label>
                                </div>
                            </div>
                        </div>

                        <div className="employee-more-leave-maincontainer">
                            <div className="employee-more-leave-container-part">
                                <div className={`employee-more-leave-floating-label-container ${isFocused?.district || district ? 'float-label' : ''}`}>
                                    <Select
                                        name="district"
                                        options={districtOptions}
                                        value={districtOptions.find(opt => opt.value === district) || null}
                                        onChange={option => handleInputChange(option, 'district')}
                                        onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                        placeholder=" "
                                        isClearable={!isITM}
                                        isDisabled={isITM}
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                    <label>Working District</label>
                                </div>
                            </div>

                            <div className="employee-more-leave-container-part">
                                <div className={`employee-more-leave-floating-label-container ${isFocused?.leaveNumber || leaveNumber ? 'float-label' : ''}`}>
                                    <input
                                        type='text'
                                        value={leaveNumber}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (isDigit(value)) {
                                                setLeaveNumber(value)
                                            }
                                        }}
                                        className="employee-more-leave-date-picker"
                                        onFocus={() => setIsFocused(prev => ({ ...prev, leaveNumber: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, leaveNumber: false }))}
                                    />
                                    <label><span className="required">*</span>Leave ≥ (in Days)</label>
                                </div>
                            </div>
                        </div>

                        <div className="employee-more-leave-maincontainer">
                            <div className="employee-more-leave-container-part">
                                <div className={`employee-more-leave-floating-label-container ${isFocused?.year || year ? 'float-label' : ''}`}>
                                    <DatePicker
                                        selected={year ? new Date(year, 0, 1) : null}
                                        onChange={handleYearChange}
                                        dateFormat="yyyy"
                                        showYearPicker
                                        placeholderText=""
                                        className="employee-more-leave-date-picker"
                                        onFocus={() => setIsFocused(prev => ({ ...prev, year: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, year: false }))}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        ref={yearInputRef}
                                    />
                                    <label><span className="required">*</span>Year</label>
                                </div>
                            </div>

                            <div className="employee-more-leave-container-part">
                                <div className={`employee-more-leave-floating-label-container ${isFocused?.month || month ? 'float-label' : ''}`}>
                                    <Select
                                        name="month"
                                        options={monthOptions}
                                        value={monthOptions.find(opt => opt.value === month) || null}
                                        onChange={option => handleInputChange(option, 'month')}
                                        placeholder=" "
                                        onFocus={() => setIsFocused(prev => ({ ...prev, month: true }))}
                                        onBlur={() => setIsFocused(prev => ({ ...prev, month: false }))}
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                    <label><span className="required">*</span>Month</label>
                                </div>
                            </div>
                        </div>

                        <div className="employee-more-leave-maincontainer">
                            <div className="employee-more-leave-container-part">
                                <div className="employee-more-leave-btn-div" style={{ display: 'flex', gap: '10px' }}>
                                    <button type="submit" className="employee-more-leave-btn" disabled={!isFetchDataEnabled}>
                                        Generate Report
                                    </button>
                                    <button
                                        type="button"
                                        className="employee-more-leave-clear-btn"
                                        onClick={handleClearAll}
                                    >
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Table Section */}
                <div className="employee-more-leave-monthlt-att-container">
                    {!showDrilldown ? (
                        <>
                            <div className="employee-more-leave-table-header">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '10px' }}>
                                    <h3>Employee Leave Report</h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                        {employeeLeaveCount.length > 0 && (
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => handleMainTableExport('pdf')}
                                                    className="export-pdf-btn"
                                                >
                                                    Download PDF
                                                </button>
                                                <button
                                                    onClick={() => handleMainTableExport('excel')}
                                                    className="export-excel-btn"
                                                >
                                                    Download Excel
                                                </button>
                                            </div>
                                        )}
                                        {employeeLeaveCount.length > 0 && year && month && (
                                            <div className="employee-more-leave-total-count">
                                                {getMonthName(month)} {year} - Total Employees: {employeeLeaveCount.length}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="employee-more-leave-table-wrapper">
                                {employeeLeaveCount.length > 0 ? (
                                    <table className="employee-more-leave-table">
                                        <thead>
                                            <tr>
                                                <th>S.No</th>
                                                <th>District</th>
                                                <th>Employee Name</th>
                                                <th>Contact Number</th>
                                                <th>Designation</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {employeeLeaveCount.map((employee, index) => (
                                                <tr
                                                    key={employee.employeeUniqueId || index}
                                                    onClick={() => handleDrilldownDetails(employee)}
                                                    className="clickable-row"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>{index + 1}</td>
                                                    <td>{employee.districtName}</td>
                                                    <td>{employee.employeeName}</td>
                                                    <td>{employee.contactNumber}</td>
                                                    <td>{employee.designation}</td>
                                                    <td className="total-count">{employee.totalAbsentLeaveCount || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="employee-more-leave-no-data">
                                        <p>No data available.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="employee-more-leave-drilldown-header">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '10px' }}>
                                    <div>
                                        <h3>Employee Leave Details</h3>
                                        {selectedEmployee && (
                                            <div className="employee-more-leave-drilldown-info" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '14px', marginTop: '5px' }}>
                                                <span><strong>Name:</strong> {selectedEmployee.employeeName}</span>
                                                <span><strong>Designation:</strong> {selectedEmployee.designation}</span>
                                                <span><strong>District:</strong> {selectedEmployee.districtName}</span>
                                                <span><strong>Total:</strong> {selectedEmployee.totalAbsentLeaveCount} days</span>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        {employeeLeavesFullDetailsonLeave?.absentLeaveDates?.length > 0 && (
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => handleDrilldownExport('pdf')}
                                                    className="export-pdf-btn"
                                                >
                                                    Download PDF
                                                </button>
                                                <button
                                                    onClick={() => handleDrilldownExport('excel')}
                                                    className="export-excel-btn"
                                                >
                                                    Download Excel
                                                </button>
                                            </div>
                                        )}
                                        <button
                                            className="employee-more-leave-back-btn"
                                            onClick={handleBackToMainTable}
                                        >
                                            ← Back to Report
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="employee-more-leave-table-wrapper">
                                {loading.drilldown ? (
                                    <div className="employee-more-leave-loading">
                                        <p>Loading details...</p>
                                    </div>
                                ) : employeeLeavesFullDetailsonLeave?.absentLeaveDates?.length > 0 ? (
                                    <table className="employee-more-leave-table">
                                        <thead>
                                            <tr>
                                                <th>S.No</th>
                                                <th>Date</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {employeeLeavesFullDetailsonLeave.absentLeaveDates.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>{format(item.date, 'dd-MM-yyyy')}</td>
                                                    <td>
                                                        <span className={`status-badge ${getStatusBadgeClass(item.status)}`}>
                                                            {item.status || 'N/A'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="employee-more-leave-no-data">
                                        <p>No leave or absence records found for this employee.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployeeLeaveCountInaMonth;